"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, type MouseEvent as ReactMouseEvent, type CSSProperties } from "react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import type { Card } from "@/lib/api/types";
import RichDescription from "@/app/components/RichDescription";
import type { RelatedCard } from "@/app/components/RichDescription";
import type { EntityNameLink } from "@/lib/rich-links";
import type { KeywordEntry, PowerEntry, SpawnedCardEntry } from "@/lib/entity-catalogs";
import { getCardDisplayModel } from "@/lib/card-display";
import { cachedFetch } from "@/lib/fetch-cache";
import LocalizedNames from "@/app/components/LocalizedNames";
import EntityUpdateHistory from "@/app/components/EntityUpdateHistory";
import RelatedCards from "@/app/components/RelatedCards";
import EntityProse from "@/app/components/EntityProse";
import EntityPairings from "@/app/components/EntityPairings";
import EntityDraftRecs from "@/app/components/EntityDraftRecs";
import EntitySummary from "@/app/components/EntitySummary";
import type { EntitySummaryData } from "@/lib/entity-summary";
import type { DraftRecs, Pairings, RelatedGroup } from "@/lib/entity-links";
import { imageUrl, fullCardUrl, enchantedCardUrl } from "@/lib/image-url";
import EntityRunStats, { type EntityStats } from "@/app/components/EntityRunStats";
import EntityVersionSelect from "@/app/components/EntityVersionSelect";
import HoverTooltip from "@/app/components/HoverTooltip";
import { useChannel, useBetaPrefix } from "@/lib/api/prefix.client";
import BetaDiffNotice from "@/app/components/BetaDiffNotice";
import "@/app/card-revamp.css";
import { useBracketParam } from "@/lib/use-bracket-param";
import { SPINE_TEXT } from "@/lib/character-colors";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Per-entity character accent for the wiki page spine (--spine).
const SPINE_COLOR: Record<string, string> = {
  ironclad: "var(--color-ironclad)",
  silent: "var(--color-silent)",
  defect: "var(--color-defect)",
  necrobinder: "var(--color-necrobinder)",
  regent: "var(--color-regent)",
  colorless: "var(--color-colorless)",
  curse: "var(--color-curse)",
  status: "var(--text-muted)"
};

// Headline figures for the infobox mini-stats block. Same endpoint EntityRunStats
// fetches, so cachedFetch dedupes it (no extra request).
interface MiniBracket {
  picks: number;
  win_rate: number;
  pick_rate: number;
  score: number | null;
  elo: number | null;
}
interface MiniStats extends MiniBracket {
  brackets?: Record<string, MiniBracket>;
}
const TOC_IDS = ["performance", "description", "relations", "pairings", "other-languages", "history"];
const typeIcons: Record<string, string> = {
  Attack: "\u2694",
  Skill: "\uD83D\uDEE1",
  Power: "\u2726",
  Status: "\u25C6",
  Curse: "\u2620",
  Quest: "\u2605"
};
const keywordTooltips: Record<string, string> = {
  Exhaust: "Remove this card from your deck when played.",
  Ethereal: "If this card is in your hand at end of turn, discard it.",
  Innate: "Always appears in your opening hand.",
  Unplayable: "Cannot be played from your hand.",
  Retain: "Keep this card in your hand at end of turn.",
  Sly: "Can be played from the discard pile.",
  Eternal: "Cannot be removed from your deck."
};
function buildInteractiveWords(keywords: string[], powerData: Record<string, {
  id: string;
  name: string;
  description: string;
  type: string;
  image_url: string | null;
}>, keywordData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, glossaryData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, orbData: Record<string, {
  id: string;
  name: string;
  description: string;
}>, bp: string): Record<string, {
  tooltip: string;
  href: string;
}> {
  const words: Record<string, {
    tooltip: string;
    href: string;
  }> = {};
  // Add keyword names (Sly, Exhaust, Ethereal, etc.)
  for (const kw of keywords) {
    const data = keywordData[kw.toLowerCase()];
    const desc = data?.description || keywordTooltips[kw] || "";
    words[kw] = {
      tooltip: desc,
      href: `${bp}/keywords/${kw.toLowerCase()}`
    };
  }
  // Add power names from [gold] tagged text (Dexterity, Thorns, Block, Strength, etc.)
  for (const [name, data] of Object.entries(powerData)) {
    words[data.name] = {
      tooltip: data.description,
      href: `${bp}/powers/${data.id.toLowerCase()}`
    };
  }
  // Add glossary terms (Block, Discard Pile, Draw Pile, Fatal, Forge, etc.)
  for (const [name, data] of Object.entries(glossaryData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/keywords/${data.id.toLowerCase()}`
      };
    }
  }
  // Add orb names (Lightning, Frost, Dark, Glass, Plasma)
  for (const [name, data] of Object.entries(orbData)) {
    if (!words[data.name]) {
      words[data.name] = {
        tooltip: data.description.replace(/\n/g, " "),
        href: `${bp}/orbs/${data.id.toLowerCase()}`
      };
    }
  }
  return words;
}
function InlineTooltip(t0) {
  const $ = _c(23);
  const {
    label,
    tooltip,
    href,
    color,
    image
  } = t0;
  const [show, setShow] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setShow(true);
    t2 = () => setShow(false);
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== color) {
    t3 = {
      color
    };
    $[2] = color;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label) {
    t4 = <span className="font-medium">{label}</span>;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== tooltip) {
    t5 = tooltip && <span className="text-[var(--text-muted)] ml-1">{tooltip.replace(/\[.*?\]/g, "").replace(/\n/g, " ").slice(0, 80)}</span>;
    $[6] = tooltip;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== image || $[9] !== label || $[10] !== show || $[11] !== tooltip) {
    t6 = show && tooltip && <span className="absolute z-[100] bottom-full left-0 mb-2 w-56 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none text-left">{image && <img src={image} alt="" className="w-6 h-6 object-contain mb-1" crossOrigin="anonymous" />}<span className="font-semibold text-xs text-[var(--text-primary)] block">{label}</span><span className="text-[10px] text-[var(--text-secondary)] leading-relaxed block mt-1"><RichDescription text={tooltip} /></span></span>;
    $[8] = image;
    $[9] = label;
    $[10] = show;
    $[11] = tooltip;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <span className="relative inline-flex items-center cursor-pointer" onMouseEnter={t1} onMouseLeave={t2} style={t3}>{t4}{t5}{t6}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const inner = t7;
  if (href) {
    let t8;
    if ($[18] !== href || $[19] !== inner) {
      t8 = <Link href={href} className="block">{inner}</Link>;
      $[18] = href;
      $[19] = inner;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  let t8;
  if ($[21] !== inner) {
    t8 = <div>{inner}</div>;
    $[21] = inner;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
const energyIconMap: Record<string, string> = {
  ironclad: "ironclad",
  silent: "silent",
  defect: "defect",
  necrobinder: "necrobinder",
  regent: "regent",
  colorless: "colorless"
};

// Merchant price ranges
function getMerchantPriceRange(rarity: string, color: string): {
  min: number;
  max: number;
} | null {
  const isColorless = color === "colorless";
  let base: number;
  switch (rarity) {
    case "Common":
      base = 50;
      break;
    case "Uncommon":
      base = 75;
      break;
    case "Rare":
      base = 150;
      break;
    default:
      return null;
  }
  if (isColorless) base = Math.round(base * 1.15);
  return {
    min: Math.floor(base * 0.95),
    max: Math.ceil(base * 1.05)
  };
}
export interface CardCatalogs {
  keywords: KeywordEntry[];
  powers: PowerEntry[];
  glossary: KeywordEntry[];
  orbs: KeywordEntry[];
  spawnedCards: SpawnedCardEntry[];
}
export default function CardDetail(t0) {
  const $ = _c(460);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialCard,
    initialEnchantments,
    initialStats,
    initialPairings,
    initialRecs,
    initialRelated,
    summary: t2,
    initialCatalogs,
    initialEntityLinks
  } = t1;
  const summary = t2 === undefined ? null : t2;
  const params = useParams();
  const id = params.id as string;
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  const channel = useChannel();
  const [card, setCard] = useState(initialCard ?? null);
  let t3;
  if ($[2] !== initialCatalogs?.spawnedCards) {
    t3 = initialCatalogs?.spawnedCards ?? [];
    $[2] = initialCatalogs?.spawnedCards;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [spawnedCards, setSpawnedCards] = useState(t3);
  const [loading, setLoading] = useState(!initialCard);
  const [notFound, setNotFound] = useState(false);
  const [upgraded, setUpgraded] = useState(false);
  const [betaArt] = useState(false);
  const [cardImgFailed, setCardImgFailed] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedEnch, setSelectedEnch] = useState("none");
  const [activeSection, setActiveSection] = useState("performance");
  const [toc, setToc] = useState(null);
  const [miniStats, setMiniStats] = useState(null);
  const [statsBracket, setStatsBracket] = useBracketParam();
  const byName = _temp;
  let t4;
  if ($[4] !== initialCatalogs?.powers) {
    t4 = () => byName(initialCatalogs?.powers ?? []);
    $[4] = initialCatalogs?.powers;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [powerData, setPowerData] = useState(t4);
  let t5;
  if ($[6] !== initialCatalogs?.keywords) {
    t5 = () => byName(initialCatalogs?.keywords ?? []);
    $[6] = initialCatalogs?.keywords;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [keywordData, setKeywordData] = useState(t5);
  let t6;
  if ($[8] !== initialCatalogs?.glossary) {
    t6 = () => byName(initialCatalogs?.glossary ?? []);
    $[8] = initialCatalogs?.glossary;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [glossaryData, setGlossaryData] = useState(t6);
  let t7;
  if ($[10] !== initialCatalogs?.orbs) {
    t7 = () => byName(initialCatalogs?.orbs ?? []);
    $[10] = initialCatalogs?.orbs;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const [orbData, setOrbData] = useState(t7);
  let t8;
  if ($[12] !== initialEnchantments) {
    t8 = initialEnchantments ?? [];
    $[12] = initialEnchantments;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const cardEnchantments = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {};
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const [enchMeta, setEnchMeta] = useState(t9);
  let t10;
  if ($[15] !== id || $[16] !== initialCard || $[17] !== initialCatalogs || $[18] !== lang) {
    t10 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/cards/${id}?lang=${lang}`).then(data => {
        setCard(data);
        if (!initialCatalogs && data.spawns_cards && data.spawns_cards.length > 0) {
          Promise.all(data.spawns_cards.map(sid => cachedFetch(`${API}/api/cards/${sid}?lang=${lang}`).catch(_temp2))).then(results => setSpawnedCards(results.filter(Boolean) as Card[]));
        }
      }).catch(() => {
        if (!initialCard) {
          setNotFound(true);
        }
      }).finally(() => setLoading(false));
    };
    $[15] = id;
    $[16] = initialCard;
    $[17] = initialCatalogs;
    $[18] = lang;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== id || $[21] !== initialCatalogs || $[22] !== lang) {
    t11 = [id, lang, initialCatalogs];
    $[20] = id;
    $[21] = initialCatalogs;
    $[22] = lang;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  if ($[24] !== cardEnchantments.length || $[25] !== initialCatalogs || $[26] !== lang) {
    t12 = () => {
      if (initialCatalogs) {
        return;
      }
      cachedFetch(`${API}/api/powers?lang=${lang}`).then(powers => {
        const m = {};
        for (const p of powers) {
          m[p.name.toLowerCase()] = p;
        }
        setPowerData(m);
      });
      cachedFetch(`${API}/api/keywords?lang=${lang}`).then(kws => {
        const m_0 = {};
        for (const k of kws) {
          m_0[k.name.toLowerCase()] = k;
        }
        setKeywordData(m_0);
      });
      cachedFetch(`${API}/api/glossary?lang=${lang}`).then(terms => {
        const m_1 = {};
        for (const t_0 of terms) {
          m_1[t_0.name.toLowerCase()] = t_0;
        }
        setGlossaryData(m_1);
      });
      cachedFetch(`${API}/api/orbs?lang=${lang}`).then(orbs => {
        const m_2 = {};
        for (const o of orbs) {
          m_2[o.name.toLowerCase()] = o;
        }
        setOrbData(m_2);
      });
      if (cardEnchantments.length > 0) {
        cachedFetch(`${API}/api/enchantments?lang=${lang}`).then(enchs => {
          const m_3 = {};
          for (const e_0 of enchs) {
            m_3[e_0.id.toLowerCase()] = e_0;
          }
          setEnchMeta(m_3);
        });
      }
    };
    $[24] = cardEnchantments.length;
    $[25] = initialCatalogs;
    $[26] = lang;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== initialCatalogs || $[29] !== lang) {
    t13 = [lang, initialCatalogs];
    $[28] = initialCatalogs;
    $[29] = lang;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[31] !== id) {
    t14 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/runs/stats/cards/${id}`).then(setMiniStats).catch(_temp3);
    };
    t15 = [id];
    $[31] = id;
    $[32] = t14;
    $[33] = t15;
  } else {
    t14 = $[32];
    t15 = $[33];
  }
  useEffect(t14, t15);
  let t16;
  if ($[34] !== card) {
    t16 = () => {
      if (!card) {
        return;
      }
      const secs = Array.from(document.querySelectorAll(".card-rvmp section[id]"));
      if (secs.length === 0) {
        return;
      }
      const obs = new IntersectionObserver(entries_0 => {
        entries_0.forEach(e_1 => {
          if (e_1.isIntersecting) {
            setActiveSection((e_1.target as HTMLElement).id);
          }
        });
      }, {
        rootMargin: "-130px 0px -70% 0px"
      });
      secs.forEach(s => obs.observe(s));
      return () => obs.disconnect();
    };
    $[34] = card;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== card || $[37] !== cardEnchantments.length) {
    t17 = [card, cardEnchantments.length];
    $[36] = card;
    $[37] = cardEnchantments.length;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[39] !== card || $[40] !== id) {
    t18 = () => {
      if (!card) {
        return;
      }
      const root = document.querySelector(".card-rvmp .main");
      if (!root) {
        return;
      }
      let raf = 0;
      const sync = () => {
        raf = 0;
        const shown = TOC_IDS.filter(_temp4);
        setToc(prev => prev && prev.id === id && prev.shown.join() === shown.join() ? prev : {
          id,
          shown
        });
      };
      const schedule = () => {
        if (!raf) {
          raf = requestAnimationFrame(sync);
        }
      };
      schedule();
      const mo = new MutationObserver(schedule);
      mo.observe(root, {
        childList: true,
        subtree: true
      });
      return () => {
        cancelAnimationFrame(raf);
        mo.disconnect();
      };
    };
    t19 = [card, id];
    $[39] = card;
    $[40] = id;
    $[41] = t18;
    $[42] = t19;
  } else {
    t18 = $[41];
    t19 = $[42];
  }
  useEffect(t18, t19);
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = (e_2, secId) => {
      e_2.preventDefault();
      const el_0 = document.getElementById(secId);
      if (el_0) {
        el_0.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
        setActiveSection(secId);
      }
    };
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const handleTocClick = t20;
  if (loading) {
    let t21;
    if ($[44] !== t) {
      t21 = t("Loading...");
      $[44] = t;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    let t22;
    if ($[46] !== t21) {
      t22 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="text-center py-12 text-[var(--text-muted)]">{t21}</div></div>;
      $[46] = t21;
      $[47] = t22;
    } else {
      t22 = $[47];
    }
    return t22;
  }
  if (notFound || !card) {
    const t21 = `${bp}/cards`;
    let t22;
    if ($[48] !== t) {
      t22 = t("Back to");
      $[48] = t;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== t) {
      t23 = t("Cards");
      $[50] = t;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== t21 || $[53] !== t22 || $[54] !== t23) {
      t24 = <Link href={t21} className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6">← {t22} {t23}</Link>;
      $[52] = t21;
      $[53] = t22;
      $[54] = t23;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    let t25;
    if ($[56] !== t) {
      t25 = t("Card Not Found");
      $[56] = t;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    let t26;
    if ($[58] !== t25) {
      t26 = <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t25}</h1>;
      $[58] = t25;
      $[59] = t26;
    } else {
      t26 = $[59];
    }
    let t27;
    if ($[60] !== id || $[61] !== t) {
      t27 = t("No card exists with ID \u201C{id}\u201D.", {
        id
      });
      $[60] = id;
      $[61] = t;
      $[62] = t27;
    } else {
      t27 = $[62];
    }
    let t28;
    if ($[63] !== t27) {
      t28 = <p className="text-[var(--text-muted)]">{t27}</p>;
      $[63] = t27;
      $[64] = t28;
    } else {
      t28 = $[64];
    }
    let t29;
    if ($[65] !== t26 || $[66] !== t28) {
      t29 = <div className="text-center py-12">{t26}{t28}</div>;
      $[65] = t26;
      $[66] = t28;
      $[67] = t29;
    } else {
      t29 = $[67];
    }
    let t30;
    if ($[68] !== t24 || $[69] !== t29) {
      t30 = <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t24}{t29}</div>;
      $[68] = t24;
      $[69] = t29;
      $[70] = t30;
    } else {
      t30 = $[70];
    }
    return t30;
  }
  let characterLabel;
  let costLabel;
  let displayKeywords;
  let displayType;
  let enchActive;
  let isUpgraded;
  let priceRange;
  let renderSrc;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let targetLabel;
  let variantImg;
  if ($[71] !== activeSection || $[72] !== betaArt || $[73] !== bp || $[74] !== card || $[75] !== cardEnchantments || $[76] !== cardImgFailed || $[77] !== channel || $[78] !== glossaryData || $[79] !== id || $[80] !== initialEntityLinks || $[81] !== initialPairings || $[82] !== initialRelated || $[83] !== initialStats || $[84] !== keywordData || $[85] !== lang || $[86] !== orbData || $[87] !== powerData || $[88] !== selectedEnch || $[89] !== selectedVariant || $[90] !== setStatsBracket || $[91] !== spawnedCards || $[92] !== statsBracket || $[93] !== summary || $[94] !== t || $[95] !== toc || $[96] !== upgraded) {
    const display = getCardDisplayModel(card, upgraded);
    const activeVariant = selectedVariant && card.type_variants ? card.type_variants[selectedVariant] : null;
    activeVariant ? activeVariant.damage : display.damage;
    activeVariant ? activeVariant.block : display.block;
    activeVariant ? card.hit_count : display.hitCount;
    const cost = activeVariant ? card.cost : display.cost;
    displayType = activeVariant ? activeVariant.type : card.type;
    isUpgraded = display.isUpgraded;
    const hasUpgrade = !!card.upgrade;
    const hasVariants = !!card.type_variants;
    variantImg = activeVariant?.image_url || null;
    const imgUrl = variantImg ? variantImg : betaArt && card.beta_image_url ? card.beta_image_url : card.image_url || card.beta_image_url;
    const descText = activeVariant ? activeVariant.description : display.descriptionText;
    const keywordText = activeVariant ? "" : display.keywordText;
    const energyIcon = energyIconMap[card.color] || "colorless";
    const t35 = card.rarity_key || card.rarity;
    let t36;
    if ($[121] !== card.color || $[122] !== t35) {
      t36 = getMerchantPriceRange(t35, card.color);
      $[121] = card.color;
      $[122] = t35;
      $[123] = t36;
    } else {
      t36 = $[123];
    }
    priceRange = t36;
    displayKeywords = [...display.visibleKeywords, ...display.addedKeywords];
    const interactiveWords = buildInteractiveWords(displayKeywords, powerData, keywordData, glossaryData, orbData, bp);
    const spineColor = SPINE_COLOR[card.color] ?? "var(--accent-gold)";
    let t37;
    if ($[124] !== card.color) {
      t37 = card.color ? card.color.charAt(0).toUpperCase() + card.color.slice(1) : "\u2014";
      $[124] = card.color;
      $[125] = t37;
    } else {
      t37 = $[125];
    }
    characterLabel = t37;
    costLabel = card.is_x_cost ? "X" : cost != null && cost < 0 ? "U" : String(cost);
    let t38;
    if ($[126] !== card.target) {
      t38 = card.target && card.target !== "None" && card.target !== "Self" ? card.target.replace(/([A-Z])/g, " $1").trim() : null;
      $[126] = card.target;
      $[127] = t38;
    } else {
      t38 = $[127];
    }
    targetLabel = t38;
    let t39;
    if ($[128] !== cardEnchantments || $[129] !== selectedEnch) {
      t39 = selectedEnch !== "none" && cardEnchantments.includes(selectedEnch);
      $[128] = cardEnchantments;
      $[129] = selectedEnch;
      $[130] = t39;
    } else {
      t39 = $[130];
    }
    enchActive = t39;
    renderSrc = enchActive ? enchantedCardUrl(card.id.toLowerCase(), selectedEnch, isUpgraded, channel, lang) : betaArt || variantImg || cardImgFailed ? imageUrl(imgUrl) : fullCardUrl(card.id.toLowerCase(), isUpgraded, channel, lang);
    const partners = initialPairings?.partners;
    let t40;
    if ($[131] !== initialPairings || $[132] !== initialRelated || $[133] !== partners?.cards?.length || $[134] !== partners?.potions?.length || $[135] !== partners?.relics?.length || $[136] !== spawnedCards.length) {
      t40 = TOC_IDS.filter(sid_1 => {
        if (sid_1 === "relations") {
          return spawnedCards.length > 0 || initialRelated === undefined || (initialRelated?.length ?? 0) > 0;
        }
        if (sid_1 === "pairings") {
          return initialPairings === undefined || !!(partners?.cards?.length || partners?.relics?.length || partners?.potions?.length);
        }
        return true;
      });
      $[131] = initialPairings;
      $[132] = initialRelated;
      $[133] = partners?.cards?.length;
      $[134] = partners?.potions?.length;
      $[135] = partners?.relics?.length;
      $[136] = spawnedCards.length;
      $[137] = t40;
    } else {
      t40 = $[137];
    }
    const serverToc = t40;
    const tocShown = toc && toc.id === id ? toc.shown : serverToc;
    let t41;
    if ($[138] !== t || $[139] !== tocShown) {
      t41 = [{
        id: "performance",
        label: t("Community")
      }, {
        id: "description",
        label: t("Description")
      }, {
        id: "relations",
        label: t("Relations")
      }, {
        id: "pairings",
        label: t("Synergy")
      }, {
        id: "other-languages",
        label: t("Other languages")
      }, {
        id: "history",
        label: t("Version history")
      }].filter(it => tocShown.includes(it.id));
      $[138] = t;
      $[139] = tocShown;
      $[140] = t41;
    } else {
      t41 = $[140];
    }
    const tocItems = t41;
    t32 = "card-rvmp";
    let t42;
    if ($[141] !== imgUrl) {
      t42 = imgUrl ? {
        "--entity-bg": `url("${imageUrl(imgUrl)}?bg")`
      } : {};
      $[141] = imgUrl;
      $[142] = t42;
    } else {
      t42 = $[142];
    }
    let t43;
    if ($[143] !== spineColor || $[144] !== t42) {
      t43 = {
        "--spine": spineColor,
        ...t42
      };
      $[143] = spineColor;
      $[144] = t42;
      $[145] = t43;
    } else {
      t43 = $[145];
    }
    t33 = t43 as CSSProperties;
    const t44 = `${bp}/cards`;
    let t45;
    if ($[146] !== t) {
      t45 = t("Back to");
      $[146] = t;
      $[147] = t45;
    } else {
      t45 = $[147];
    }
    let t46;
    if ($[148] !== t) {
      t46 = t("Cards");
      $[148] = t;
      $[149] = t46;
    } else {
      t46 = $[149];
    }
    let t47;
    if ($[150] !== t44 || $[151] !== t45 || $[152] !== t46) {
      t47 = <Link href={t44} className="cd-back">← {t45} {t46}</Link>;
      $[150] = t44;
      $[151] = t45;
      $[152] = t46;
      $[153] = t47;
    } else {
      t47 = $[153];
    }
    let t48;
    if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = {
        marginTop: 12
      };
      $[154] = t48;
    } else {
      t48 = $[154];
    }
    let t49;
    if ($[155] !== card.id) {
      t49 = <div style={t48}><BetaDiffNotice entityType="cards" entityId={card.id} /></div>;
      $[155] = card.id;
      $[156] = t49;
    } else {
      t49 = $[156];
    }
    if ($[157] !== t47 || $[158] !== t49) {
      t34 = <div className="cd-top">{t47}{t49}</div>;
      $[157] = t47;
      $[158] = t49;
      $[159] = t34;
    } else {
      t34 = $[159];
    }
    t31 = "wrap";
    t27 = "main";
    let t50;
    if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
      t50 = <span className="dot">◆</span>;
      $[160] = t50;
    } else {
      t50 = $[160];
    }
    let t51;
    if ($[161] !== characterLabel) {
      t51 = <span>{characterLabel}</span>;
      $[161] = characterLabel;
      $[162] = t51;
    } else {
      t51 = $[162];
    }
    let t52;
    if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
      t52 = <span className="sep">·</span>;
      $[163] = t52;
    } else {
      t52 = $[163];
    }
    let t53;
    if ($[164] !== card.rarity) {
      t53 = <span>{card.rarity}</span>;
      $[164] = card.rarity;
      $[165] = t53;
    } else {
      t53 = $[165];
    }
    let t54;
    if ($[166] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <span className="sep">·</span>;
      $[166] = t54;
    } else {
      t54 = $[166];
    }
    const t55 = typeIcons[displayType] || "";
    let t56;
    if ($[167] !== displayType || $[168] !== t55) {
      t56 = <span>{t55} {displayType}</span>;
      $[167] = displayType;
      $[168] = t55;
      $[169] = t56;
    } else {
      t56 = $[169];
    }
    let t57;
    if ($[170] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <span className="sep">·</span>;
      $[170] = t57;
    } else {
      t57 = $[170];
    }
    let t58;
    if ($[171] !== t) {
      t58 = t("Energy");
      $[171] = t;
      $[172] = t58;
    } else {
      t58 = $[172];
    }
    let t59;
    if ($[173] !== costLabel || $[174] !== t58) {
      t59 = <span>{costLabel} {t58}</span>;
      $[173] = costLabel;
      $[174] = t58;
      $[175] = t59;
    } else {
      t59 = $[175];
    }
    let t60;
    if ($[176] !== card.is_x_star_cost || $[177] !== card.star_cost) {
      t60 = (card.star_cost != null || card.is_x_star_cost) && <><span className="sep">·</span><span>{card.is_x_star_cost ? "X" : card.star_cost} ★</span></>;
      $[176] = card.is_x_star_cost;
      $[177] = card.star_cost;
      $[178] = t60;
    } else {
      t60 = $[178];
    }
    let t61;
    if ($[179] !== t51 || $[180] !== t53 || $[181] !== t56 || $[182] !== t59 || $[183] !== t60) {
      t61 = <p className="eyebrow">{t50}{t51}{t52}{t53}{t54}{t56}{t57}{t59}{t60}</p>;
      $[179] = t51;
      $[180] = t53;
      $[181] = t56;
      $[182] = t59;
      $[183] = t60;
      $[184] = t61;
    } else {
      t61 = $[184];
    }
    let t62;
    if ($[185] !== hasUpgrade || $[186] !== t || $[187] !== upgraded) {
      t62 = hasUpgrade && <div className="seg hero-seg" role="group" aria-label={t("Card version")}><button type="button" className={`segbtn${!upgraded ? " on" : ""}`} onClick={() => setUpgraded(false)}>{t("Normal")}</button><button type="button" className={`segbtn${upgraded ? " on" : ""}`} onClick={() => setUpgraded(true)}>{t("Upgraded")}</button></div>;
      $[185] = hasUpgrade;
      $[186] = t;
      $[187] = upgraded;
      $[188] = t62;
    } else {
      t62 = $[188];
    }
    const t63 = <div className="hero-head"><h1>{card.name}{isUpgraded && <span className="up">+</span>}</h1>{t62}</div>;
    let t64;
    if ($[189] !== card || $[190] !== upgraded) {
      t64 = <EntityProse kind="card" card={card} upgraded={upgraded} lead={true} />;
      $[189] = card;
      $[190] = upgraded;
      $[191] = t64;
    } else {
      t64 = $[191];
    }
    if ($[192] !== t61 || $[193] !== t63 || $[194] !== t64) {
      t28 = <div className="hero">{t61}{t63}{t64}</div>;
      $[192] = t61;
      $[193] = t63;
      $[194] = t64;
      $[195] = t28;
    } else {
      t28 = $[195];
    }
    let t65;
    if ($[196] !== t) {
      t65 = t("On this page");
      $[196] = t;
      $[197] = t65;
    } else {
      t65 = $[197];
    }
    let t66;
    if ($[198] !== t) {
      t66 = t("On this page");
      $[198] = t;
      $[199] = t66;
    } else {
      t66 = $[199];
    }
    let t67;
    if ($[200] !== t66) {
      t67 = <span className="toc-label">{t66}</span>;
      $[200] = t66;
      $[201] = t67;
    } else {
      t67 = $[201];
    }
    let t68;
    if ($[202] !== activeSection || $[203] !== tocItems) {
      t68 = tocItems.map(it_0 => <a key={it_0.id} href={`#${it_0.id}`} className={activeSection === it_0.id ? "on" : undefined} onClick={e_3 => handleTocClick(e_3, it_0.id)}>{it_0.label}</a>);
      $[202] = activeSection;
      $[203] = tocItems;
      $[204] = t68;
    } else {
      t68 = $[204];
    }
    if ($[205] !== t65 || $[206] !== t67 || $[207] !== t68) {
      t29 = <nav className="toc" aria-label={t65}>{t67}{t68}</nav>;
      $[205] = t65;
      $[206] = t67;
      $[207] = t68;
      $[208] = t29;
    } else {
      t29 = $[208];
    }
    let t69;
    if ($[209] !== t) {
      t69 = t("Community performance");
      $[209] = t;
      $[210] = t69;
    } else {
      t69 = $[210];
    }
    let t70;
    if ($[211] !== t69) {
      t70 = <h2>{t69}</h2>;
      $[211] = t69;
      $[212] = t70;
    } else {
      t70 = $[212];
    }
    let t71;
    if ($[213] !== t) {
      t71 = t("Live aggregate across community-submitted runs. Filter by bracket to see how it holds up at higher levels of play.");
      $[213] = t;
      $[214] = t71;
    } else {
      t71 = $[214];
    }
    let t72;
    if ($[215] !== t71) {
      t72 = <p className="h-note">{t71}</p>;
      $[215] = t71;
      $[216] = t72;
    } else {
      t72 = $[216];
    }
    let t73;
    if ($[217] !== card.name || $[218] !== id || $[219] !== initialStats || $[220] !== setStatsBracket || $[221] !== statsBracket) {
      t73 = <EntityRunStats entityType="cards" entityId={id} entityName={card.name} variant="wiki" initialStats={initialStats} bracket={statsBracket} onBracketChange={setStatsBracket} />;
      $[217] = card.name;
      $[218] = id;
      $[219] = initialStats;
      $[220] = setStatsBracket;
      $[221] = statsBracket;
      $[222] = t73;
    } else {
      t73 = $[222];
    }
    if ($[223] !== t70 || $[224] !== t72 || $[225] !== t73) {
      t30 = <section id="performance">{t70}{t72}{t73}</section>;
      $[223] = t70;
      $[224] = t72;
      $[225] = t73;
      $[226] = t30;
    } else {
      t30 = $[226];
    }
    t21 = "description";
    let t74;
    if ($[227] !== t) {
      t74 = t("Description");
      $[227] = t;
      $[228] = t74;
    } else {
      t74 = $[228];
    }
    if ($[229] !== t74) {
      t22 = <h2>{t74}</h2>;
      $[229] = t74;
      $[230] = t22;
    } else {
      t22 = $[230];
    }
    if ($[231] !== card.name || $[232] !== summary) {
      t23 = <EntitySummary name={card.name} data={summary} />;
      $[231] = card.name;
      $[232] = summary;
      $[233] = t23;
    } else {
      t23 = $[233];
    }
    if ($[234] !== card.type || $[235] !== card.type_variants || $[236] !== hasVariants || $[237] !== selectedVariant) {
      t24 = hasVariants && card.type_variants && <div className="vtoggle">{Object.entries(card.type_variants).map(t75 => {
          const [key, v] = t75;
          const on = selectedVariant === key || !selectedVariant && key === card.type.toLowerCase();
          return <button key={key} type="button" className={`vbtn${on ? " on" : ""}`} onClick={() => setSelectedVariant(selectedVariant === key ? null : key)}>{typeIcons[v.type] || ""} {v.type}</button>;
        })}</div>;
      $[234] = card.type;
      $[235] = card.type_variants;
      $[236] = hasVariants;
      $[237] = selectedVariant;
      $[238] = t24;
    } else {
      t24 = $[238];
    }
    t25 = hasVariants && card.type_variants ? <div className="space-y-3">{Object.entries(card.type_variants).map(t75 => {
        const [key_0, v_0] = t75;
        const isActive = selectedVariant === key_0 || !selectedVariant && key_0 === card.type.toLowerCase();
        return <div key={key_0} className={`text-sm leading-relaxed rounded-lg border transition-colors overflow-hidden ${isActive ? "border-[var(--accent-gold)]/30" : "border-[var(--border-subtle)] opacity-60"}`}><div className={`px-3 py-2 ${isActive ? "bg-[var(--accent-gold)]/5" : "bg-[var(--bg-primary)]/50"}`}><span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mr-2">{typeIcons[v_0.type] || ""} {v_0.type}</span>{v_0.description ? <span className="text-[var(--text-secondary)]"><RichDescription text={v_0.description} energyIcon={energyIcon} /></span> : null}</div>{v_0.riders && v_0.riders.length > 0 && <div className="border-t border-[var(--border-subtle)] px-3 py-2 space-y-1.5">{v_0.riders.map(r => <div key={r.id} className="flex items-start gap-2 text-xs"><span className="font-medium text-[var(--accent-gold)] whitespace-nowrap flex-shrink-0">+ {r.name}</span><span className="text-[var(--text-secondary)]"><RichDescription text={r.description} energyIcon={energyIcon} /></span></div>)}</div>}</div>;
      })}</div> : <><div className="desc-quote"><RichDescription text={descText} energyIcon={energyIcon} relatedCards={spawnedCards} interactiveWords={interactiveWords} entityLinks={initialEntityLinks} /></div>{keywordText && <div className="desc-body"><RichDescription text={keywordText} energyIcon={energyIcon} interactiveWords={interactiveWords} entityLinks={initialEntityLinks} /></div>}</>;
    t26 = displayKeywords.length > 0 && <div className="kw-row">{displayKeywords.map(kw => {
        const data_0 = keywordData[kw.toLowerCase()];
        const tip = data_0?.description || keywordTooltips[kw] || "";
        return <HoverTooltip key={kw} title={data_0?.name || kw} content={tip}><Link href={`${bp}/keywords/${(data_0?.id || kw).toLowerCase()}`} className="kw">{data_0?.name || kw}</Link></HoverTooltip>;
      })}</div>;
    $[71] = activeSection;
    $[72] = betaArt;
    $[73] = bp;
    $[74] = card;
    $[75] = cardEnchantments;
    $[76] = cardImgFailed;
    $[77] = channel;
    $[78] = glossaryData;
    $[79] = id;
    $[80] = initialEntityLinks;
    $[81] = initialPairings;
    $[82] = initialRelated;
    $[83] = initialStats;
    $[84] = keywordData;
    $[85] = lang;
    $[86] = orbData;
    $[87] = powerData;
    $[88] = selectedEnch;
    $[89] = selectedVariant;
    $[90] = setStatsBracket;
    $[91] = spawnedCards;
    $[92] = statsBracket;
    $[93] = summary;
    $[94] = t;
    $[95] = toc;
    $[96] = upgraded;
    $[97] = characterLabel;
    $[98] = costLabel;
    $[99] = displayKeywords;
    $[100] = displayType;
    $[101] = enchActive;
    $[102] = isUpgraded;
    $[103] = priceRange;
    $[104] = renderSrc;
    $[105] = t21;
    $[106] = t22;
    $[107] = t23;
    $[108] = t24;
    $[109] = t25;
    $[110] = t26;
    $[111] = t27;
    $[112] = t28;
    $[113] = t29;
    $[114] = t30;
    $[115] = t31;
    $[116] = t32;
    $[117] = t33;
    $[118] = t34;
    $[119] = targetLabel;
    $[120] = variantImg;
  } else {
    characterLabel = $[97];
    costLabel = $[98];
    displayKeywords = $[99];
    displayType = $[100];
    enchActive = $[101];
    isUpgraded = $[102];
    priceRange = $[103];
    renderSrc = $[104];
    t21 = $[105];
    t22 = $[106];
    t23 = $[107];
    t24 = $[108];
    t25 = $[109];
    t26 = $[110];
    t27 = $[111];
    t28 = $[112];
    t29 = $[113];
    t30 = $[114];
    t31 = $[115];
    t32 = $[116];
    t33 = $[117];
    t34 = $[118];
    targetLabel = $[119];
    variantImg = $[120];
  }
  let t35;
  if ($[239] !== bp || $[240] !== card.sources || $[241] !== t) {
    t35 = card.sources && card.sources.length > 0 && <><h3 className="subh">{t("Sources")}</h3><p className="h-note">{t("Added to your deck in combat by:")}</p><div className="kw-row">{card.sources.map(s_0 => <Link key={s_0.id} href={`${bp}/monsters/${s_0.id.toLowerCase()}`} className="kw">{s_0.name}</Link>)}</div></>;
    $[239] = bp;
    $[240] = card.sources;
    $[241] = t;
    $[242] = t35;
  } else {
    t35 = $[242];
  }
  let t36;
  if ($[243] !== bp || $[244] !== card.powers_applied || $[245] !== powerData || $[246] !== t) {
    t36 = card.powers_applied && card.powers_applied.length > 0 && <><h3 className="subh">{t("Powers Applied")}</h3><div className="pow-list">{card.powers_applied.map(pa => {
          const powerName = pa.power_key || pa.power;
          const powerId = powerName.replace(/([A-Z])/g, "_$1").replace(/^_/, "").toUpperCase();
          const prettyName = pa.power.replace(/([A-Z])/g, " $1").trim();
          const data_1 = powerData[pa.power.toLowerCase()];
          return <HoverTooltip key={pa.power} title={prettyName} content={data_1?.description}><Link href={`${bp}/powers/${powerId}`}>{prettyName}{pa.amount ? ` ${pa.amount}` : ""}</Link></HoverTooltip>;
        })}</div></>;
    $[243] = bp;
    $[244] = card.powers_applied;
    $[245] = powerData;
    $[246] = t;
    $[247] = t36;
  } else {
    t36 = $[247];
  }
  let t37;
  if ($[248] !== t21 || $[249] !== t22 || $[250] !== t23 || $[251] !== t24 || $[252] !== t25 || $[253] !== t26 || $[254] !== t35 || $[255] !== t36) {
    t37 = <section id={t21}>{t22}{t23}{t24}{t25}{t26}{t35}{t36}</section>;
    $[248] = t21;
    $[249] = t22;
    $[250] = t23;
    $[251] = t24;
    $[252] = t25;
    $[253] = t26;
    $[254] = t35;
    $[255] = t36;
    $[256] = t37;
  } else {
    t37 = $[256];
  }
  let t38;
  if ($[257] !== t) {
    t38 = t("Relations");
    $[257] = t;
    $[258] = t38;
  } else {
    t38 = $[258];
  }
  let t39;
  if ($[259] !== t38) {
    t39 = <h2>{t38}</h2>;
    $[259] = t38;
    $[260] = t39;
  } else {
    t39 = $[260];
  }
  let t40;
  if ($[261] !== t) {
    t40 = t("What this card makes, and what else interacts with it.");
    $[261] = t;
    $[262] = t40;
  } else {
    t40 = $[262];
  }
  let t41;
  if ($[263] !== t40) {
    t41 = <p className="h-note">{t40}</p>;
    $[263] = t40;
    $[264] = t41;
  } else {
    t41 = $[264];
  }
  let t42;
  if ($[265] !== bp || $[266] !== spawnedCards || $[267] !== t) {
    t42 = spawnedCards.length > 0 && <div className="rel-block"><div className="rl">{t("Generates")}</div><div className="chips">{spawnedCards.map(sc => <Link key={sc.id} href={`${bp}/cards/${sc.id.toLowerCase()}`} className="cardlink">{sc.image_url && <img className="cardimg xs" src={imageUrl(sc.image_url)} alt="" crossOrigin="anonymous" />}<span><span className="cln">{sc.name}</span><span className="cls">{sc.type}</span></span></Link>)}</div></div>;
    $[265] = bp;
    $[266] = spawnedCards;
    $[267] = t;
    $[268] = t42;
  } else {
    t42 = $[268];
  }
  let t43;
  if ($[269] !== card.color || $[270] !== card.tags || $[271] !== displayKeywords || $[272] !== id || $[273] !== initialRelated) {
    t43 = <RelatedCards currentId={id} keywords={displayKeywords} tags={card.tags} color={card.color} initialGroups={initialRelated} />;
    $[269] = card.color;
    $[270] = card.tags;
    $[271] = displayKeywords;
    $[272] = id;
    $[273] = initialRelated;
    $[274] = t43;
  } else {
    t43 = $[274];
  }
  let t44;
  if ($[275] !== t42 || $[276] !== t43) {
    t44 = <div className="rel">{t42}{t43}</div>;
    $[275] = t42;
    $[276] = t43;
    $[277] = t44;
  } else {
    t44 = $[277];
  }
  let t45;
  if ($[278] !== t39 || $[279] !== t41 || $[280] !== t44) {
    t45 = <section id="relations">{t39}{t41}{t44}</section>;
    $[278] = t39;
    $[279] = t41;
    $[280] = t44;
    $[281] = t45;
  } else {
    t45 = $[281];
  }
  let t46;
  if ($[282] !== bp || $[283] !== card.name || $[284] !== id || $[285] !== initialPairings || $[286] !== lang) {
    t46 = <EntityPairings kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialPairings} />;
    $[282] = bp;
    $[283] = card.name;
    $[284] = id;
    $[285] = initialPairings;
    $[286] = lang;
    $[287] = t46;
  } else {
    t46 = $[287];
  }
  let t47;
  if ($[288] !== bp || $[289] !== card.name || $[290] !== id || $[291] !== initialRecs || $[292] !== lang) {
    t47 = <EntityDraftRecs kind="cards" id={id} name={card.name} lang={lang} bp={bp} initial={initialRecs} />;
    $[288] = bp;
    $[289] = card.name;
    $[290] = id;
    $[291] = initialRecs;
    $[292] = lang;
    $[293] = t47;
  } else {
    t47 = $[293];
  }
  let t48;
  let t49;
  if ($[294] !== id) {
    t48 = <LocalizedNames entityType="cards" entityId={id} />;
    t49 = <EntityUpdateHistory entityType="cards" entityId={id} />;
    $[294] = id;
    $[295] = t48;
    $[296] = t49;
  } else {
    t48 = $[295];
    t49 = $[296];
  }
  let t50;
  if ($[297] !== t27 || $[298] !== t28 || $[299] !== t29 || $[300] !== t30 || $[301] !== t37 || $[302] !== t45 || $[303] !== t46 || $[304] !== t47 || $[305] !== t48 || $[306] !== t49) {
    t50 = <main className={t27}>{t28}{t29}{t30}{t37}{t45}{t46}{t47}{t48}{t49}</main>;
    $[297] = t27;
    $[298] = t28;
    $[299] = t29;
    $[300] = t30;
    $[301] = t37;
    $[302] = t45;
    $[303] = t46;
    $[304] = t47;
    $[305] = t48;
    $[306] = t49;
    $[307] = t50;
  } else {
    t50 = $[307];
  }
  const t51 = `${card.name}${isUpgraded ? "+" : ""}`;
  let t52;
  if ($[308] !== t || $[309] !== t51) {
    t52 = t("{name} - Slay the Spire 2", {
      name: t51
    });
    $[308] = t;
    $[309] = t51;
    $[310] = t52;
  } else {
    t52 = $[310];
  }
  let t53;
  if ($[311] !== betaArt || $[312] !== card.id || $[313] !== channel || $[314] !== enchActive || $[315] !== lang || $[316] !== selectedEnch || $[317] !== variantImg) {
    t53 = e_4 => {
      const el_1 = e_4.currentTarget;
      if (enchActive) {
        if (!el_1.dataset.fb) {
          el_1.dataset.fb = "1";
          el_1.src = enchantedCardUrl(card.id.toLowerCase(), selectedEnch, false, channel, lang);
        }
      } else {
        if (!betaArt && !variantImg) {
          setCardImgFailed(true);
        }
      }
    };
    $[311] = betaArt;
    $[312] = card.id;
    $[313] = channel;
    $[314] = enchActive;
    $[315] = lang;
    $[316] = selectedEnch;
    $[317] = variantImg;
    $[318] = t53;
  } else {
    t53 = $[318];
  }
  let t54;
  if ($[319] !== renderSrc || $[320] !== t52 || $[321] !== t53) {
    t54 = <img key={renderSrc} className="cardimg render cardframe" src={renderSrc} alt={t52} crossOrigin="anonymous" onError={t53} />;
    $[319] = renderSrc;
    $[320] = t52;
    $[321] = t53;
    $[322] = t54;
  } else {
    t54 = $[322];
  }
  let t55;
  if ($[323] !== cardEnchantments || $[324] !== enchMeta || $[325] !== selectedEnch || $[326] !== t) {
    t55 = cardEnchantments.length > 0 && <select className="ench-select" aria-label={t("Enchantment")} value={selectedEnch} onChange={e_5 => setSelectedEnch(e_5.target.value)}><option value="none">{t("No enchantment")}</option>{cardEnchantments.map(eid => <option key={eid} value={eid}>{enchMeta[eid]?.name ?? eid}</option>)}</select>;
    $[323] = cardEnchantments;
    $[324] = enchMeta;
    $[325] = selectedEnch;
    $[326] = t;
    $[327] = t55;
  } else {
    t55 = $[327];
  }
  let t56;
  if ($[328] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = d => {
      if (d) {
        setCard(d as Card);
      }
    };
    $[328] = t56;
  } else {
    t56 = $[328];
  }
  let t57;
  if ($[329] !== id || $[330] !== setStatsBracket || $[331] !== statsBracket) {
    t57 = <EntityVersionSelect entityType="cards" entityId={id} bracket={statsBracket} onBracketChange={setStatsBracket} onEntityData={t56} />;
    $[329] = id;
    $[330] = setStatsBracket;
    $[331] = statsBracket;
    $[332] = t57;
  } else {
    t57 = $[332];
  }
  let t58;
  if ($[333] !== t55 || $[334] !== t57) {
    t58 = <div className="variant">{t55}{t57}</div>;
    $[333] = t55;
    $[334] = t57;
    $[335] = t58;
  } else {
    t58 = $[335];
  }
  let t59;
  if ($[336] !== t) {
    t59 = t("At a glance");
    $[336] = t;
    $[337] = t59;
  } else {
    t59 = $[337];
  }
  let t60;
  if ($[338] !== t59) {
    t60 = <div className="fh">{t59}</div>;
    $[338] = t59;
    $[339] = t60;
  } else {
    t60 = $[339];
  }
  let t61;
  if ($[340] !== t) {
    t61 = t("Energy");
    $[340] = t;
    $[341] = t61;
  } else {
    t61 = $[341];
  }
  let t62;
  if ($[342] !== t61) {
    t62 = <dt>{t61}</dt>;
    $[342] = t61;
    $[343] = t62;
  } else {
    t62 = $[343];
  }
  let t63;
  if ($[344] !== costLabel) {
    t63 = <dd>{costLabel}</dd>;
    $[344] = costLabel;
    $[345] = t63;
  } else {
    t63 = $[345];
  }
  let t64;
  if ($[346] !== t62 || $[347] !== t63) {
    t64 = <div className="frow">{t62}{t63}</div>;
    $[346] = t62;
    $[347] = t63;
    $[348] = t64;
  } else {
    t64 = $[348];
  }
  let t65;
  if ($[349] !== card.is_x_star_cost || $[350] !== card.star_cost || $[351] !== t) {
    t65 = (card.star_cost != null || card.is_x_star_cost) && <div className="frow"><dt>{t("Star Cost")}</dt><dd>{card.is_x_star_cost ? "X" : card.star_cost}</dd></div>;
    $[349] = card.is_x_star_cost;
    $[350] = card.star_cost;
    $[351] = t;
    $[352] = t65;
  } else {
    t65 = $[352];
  }
  let t66;
  if ($[353] !== t) {
    t66 = t("Type");
    $[353] = t;
    $[354] = t66;
  } else {
    t66 = $[354];
  }
  let t67;
  if ($[355] !== t66) {
    t67 = <dt>{t66}</dt>;
    $[355] = t66;
    $[356] = t67;
  } else {
    t67 = $[356];
  }
  let t68;
  if ($[357] !== displayType) {
    t68 = <dd>{displayType}</dd>;
    $[357] = displayType;
    $[358] = t68;
  } else {
    t68 = $[358];
  }
  let t69;
  if ($[359] !== t67 || $[360] !== t68) {
    t69 = <div className="frow">{t67}{t68}</div>;
    $[359] = t67;
    $[360] = t68;
    $[361] = t69;
  } else {
    t69 = $[361];
  }
  let t70;
  if ($[362] !== t) {
    t70 = t("Rarity");
    $[362] = t;
    $[363] = t70;
  } else {
    t70 = $[363];
  }
  let t71;
  if ($[364] !== t70) {
    t71 = <dt>{t70}</dt>;
    $[364] = t70;
    $[365] = t71;
  } else {
    t71 = $[365];
  }
  let t72;
  if ($[366] !== card.rarity) {
    t72 = <dd>{card.rarity}</dd>;
    $[366] = card.rarity;
    $[367] = t72;
  } else {
    t72 = $[367];
  }
  let t73;
  if ($[368] !== t71 || $[369] !== t72) {
    t73 = <div className="frow">{t71}{t72}</div>;
    $[368] = t71;
    $[369] = t72;
    $[370] = t73;
  } else {
    t73 = $[370];
  }
  let t74;
  if ($[371] !== t) {
    t74 = t("Character");
    $[371] = t;
    $[372] = t74;
  } else {
    t74 = $[372];
  }
  let t75;
  if ($[373] !== t74) {
    t75 = <dt>{t74}</dt>;
    $[373] = t74;
    $[374] = t75;
  } else {
    t75 = $[374];
  }
  let t76;
  if ($[375] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = {
      color: SPINE_TEXT
    };
    $[375] = t76;
  } else {
    t76 = $[375];
  }
  let t77;
  if ($[376] !== characterLabel) {
    t77 = <dd style={t76}>{characterLabel}</dd>;
    $[376] = characterLabel;
    $[377] = t77;
  } else {
    t77 = $[377];
  }
  let t78;
  if ($[378] !== t75 || $[379] !== t77) {
    t78 = <div className="frow">{t75}{t77}</div>;
    $[378] = t75;
    $[379] = t77;
    $[380] = t78;
  } else {
    t78 = $[380];
  }
  let t79;
  if ($[381] !== t || $[382] !== targetLabel) {
    t79 = targetLabel && <div className="frow"><dt>{t("Target")}</dt><dd>{targetLabel}</dd></div>;
    $[381] = t;
    $[382] = targetLabel;
    $[383] = t79;
  } else {
    t79 = $[383];
  }
  let t80;
  if ($[384] !== displayKeywords || $[385] !== t) {
    t80 = displayKeywords.length > 0 && <div className="frow"><dt>{t("Keywords")}</dt><dd>{displayKeywords.map(_temp5)}</dd></div>;
    $[384] = displayKeywords;
    $[385] = t;
    $[386] = t80;
  } else {
    t80 = $[386];
  }
  let t81;
  if ($[387] !== bp || $[388] !== spawnedCards || $[389] !== t) {
    t81 = spawnedCards.length > 0 && <div className="frow"><dt>{t("Generates")}</dt><dd>{spawnedCards.map((sc_0, i) => <span key={sc_0.id}>{i > 0 ? ", " : ""}<Link href={`${bp}/cards/${sc_0.id.toLowerCase()}`}>{sc_0.name}</Link></span>)}</dd></div>;
    $[387] = bp;
    $[388] = spawnedCards;
    $[389] = t;
    $[390] = t81;
  } else {
    t81 = $[390];
  }
  let t82;
  if ($[391] !== priceRange || $[392] !== t) {
    t82 = priceRange && <div className="frow"><dt>{t("Merchant Price")}</dt><dd><img src={imageUrl("/static/images/ui/rewards/reward_icon_money.webp")} alt={t("Gold")} style={{
          width: 15,
          height: 15
        }} crossOrigin="anonymous" />{priceRange.min}–{priceRange.max}</dd></div>;
    $[391] = priceRange;
    $[392] = t;
    $[393] = t82;
  } else {
    t82 = $[393];
  }
  let t83;
  if ($[394] !== t64 || $[395] !== t65 || $[396] !== t69 || $[397] !== t73 || $[398] !== t78 || $[399] !== t79 || $[400] !== t80 || $[401] !== t81 || $[402] !== t82) {
    t83 = <dl>{t64}{t65}{t69}{t73}{t78}{t79}{t80}{t81}{t82}</dl>;
    $[394] = t64;
    $[395] = t65;
    $[396] = t69;
    $[397] = t73;
    $[398] = t78;
    $[399] = t79;
    $[400] = t80;
    $[401] = t81;
    $[402] = t82;
    $[403] = t83;
  } else {
    t83 = $[403];
  }
  let t84;
  if ($[404] !== t60 || $[405] !== t83) {
    t84 = <div className="facts">{t60}{t83}</div>;
    $[404] = t60;
    $[405] = t83;
    $[406] = t84;
  } else {
    t84 = $[406];
  }
  let t85;
  bb0: {
    const mini = miniStats?.brackets?.[statsBracket] ?? miniStats;
    if (!mini || mini.picks <= 0) {
      t85 = null;
      break bb0;
    }
    let t86;
    if ($[407] !== t) {
      t86 = t("Community");
      $[407] = t;
      $[408] = t86;
    } else {
      t86 = $[408];
    }
    let t87;
    if ($[409] !== t86) {
      t87 = <div className="mh">{t86}</div>;
      $[409] = t86;
      $[410] = t87;
    } else {
      t87 = $[410];
    }
    const t88 = mini.win_rate >= 50 ? "var(--good)" : "var(--warn)";
    let t89;
    if ($[411] !== t88) {
      t89 = {
        color: t88
      };
      $[411] = t88;
      $[412] = t89;
    } else {
      t89 = $[412];
    }
    let t90;
    if ($[413] !== mini.win_rate || $[414] !== t89) {
      t90 = <div className="mv" style={t89}>{mini.win_rate}%</div>;
      $[413] = mini.win_rate;
      $[414] = t89;
      $[415] = t90;
    } else {
      t90 = $[415];
    }
    let t91;
    if ($[416] !== t) {
      t91 = t("Win rate");
      $[416] = t;
      $[417] = t91;
    } else {
      t91 = $[417];
    }
    let t92;
    if ($[418] !== t91) {
      t92 = <div className="ml">{t91}</div>;
      $[418] = t91;
      $[419] = t92;
    } else {
      t92 = $[419];
    }
    let t93;
    if ($[420] !== t90 || $[421] !== t92) {
      t93 = <div>{t90}{t92}</div>;
      $[420] = t90;
      $[421] = t92;
      $[422] = t93;
    } else {
      t93 = $[422];
    }
    let t94;
    if ($[423] !== mini.pick_rate) {
      t94 = <div className="mv">{mini.pick_rate}%</div>;
      $[423] = mini.pick_rate;
      $[424] = t94;
    } else {
      t94 = $[424];
    }
    let t95;
    if ($[425] !== t) {
      t95 = t("Pick rate");
      $[425] = t;
      $[426] = t95;
    } else {
      t95 = $[426];
    }
    let t96;
    if ($[427] !== t95) {
      t96 = <div className="ml">{t95}</div>;
      $[427] = t95;
      $[428] = t96;
    } else {
      t96 = $[428];
    }
    let t97;
    if ($[429] !== t94 || $[430] !== t96) {
      t97 = <div>{t94}{t96}</div>;
      $[429] = t94;
      $[430] = t96;
      $[431] = t97;
    } else {
      t97 = $[431];
    }
    let t98;
    if ($[432] !== mini.score || $[433] !== t) {
      t98 = mini.score != null && <div><div className="mv">{mini.score}</div><div className="ml">{t("Codex Score")}</div></div>;
      $[432] = mini.score;
      $[433] = t;
      $[434] = t98;
    } else {
      t98 = $[434];
    }
    let t99;
    if ($[435] !== mini.elo || $[436] !== t) {
      t99 = mini.elo != null && <div><div className="mv">{Math.round(mini.elo)}</div><div className="ml">{t("Elo")}</div></div>;
      $[435] = mini.elo;
      $[436] = t;
      $[437] = t99;
    } else {
      t99 = $[437];
    }
    let t100;
    if ($[438] !== t93 || $[439] !== t97 || $[440] !== t98 || $[441] !== t99) {
      t100 = <div className="mg">{t93}{t97}{t98}{t99}</div>;
      $[438] = t93;
      $[439] = t97;
      $[440] = t98;
      $[441] = t99;
      $[442] = t100;
    } else {
      t100 = $[442];
    }
    let t101;
    if ($[443] !== t100 || $[444] !== t87) {
      t101 = <div className="mini">{t87}{t100}</div>;
      $[443] = t100;
      $[444] = t87;
      $[445] = t101;
    } else {
      t101 = $[445];
    }
    t85 = t101;
  }
  let t86;
  if ($[446] !== t54 || $[447] !== t58 || $[448] !== t84 || $[449] !== t85) {
    t86 = <aside className="aside"><div className="box">{t54}{t58}{t84}{t85}</div></aside>;
    $[446] = t54;
    $[447] = t58;
    $[448] = t84;
    $[449] = t85;
    $[450] = t86;
  } else {
    t86 = $[450];
  }
  let t87;
  if ($[451] !== t31 || $[452] !== t50 || $[453] !== t86) {
    t87 = <div className={t31}>{t50}{t86}</div>;
    $[451] = t31;
    $[452] = t50;
    $[453] = t86;
    $[454] = t87;
  } else {
    t87 = $[454];
  }
  let t88;
  if ($[455] !== t32 || $[456] !== t33 || $[457] !== t34 || $[458] !== t87) {
    t88 = <div className={t32} style={t33}>{t34}{t87}</div>;
    $[455] = t32;
    $[456] = t33;
    $[457] = t34;
    $[458] = t87;
    $[459] = t88;
  } else {
    t88 = $[459];
  }
  return t88;
}
function _temp5(kw_0) {
  return <span className="kw" key={kw_0}>{kw_0}</span>;
}
function _temp4(sid_0) {
  const el = document.getElementById(sid_0);
  return !!el && el.getClientRects().length > 0;
}
function _temp3() {}
function _temp2() {
  return null;
}
function _temp(entries) {
  const map = {};
  for (const e of entries) {
    map[e.name.toLowerCase()] = e;
  }
  return map;
}